import { z } from 'zod';
import { auditQuerySchema, emergencyStopSchema } from './admin.js';
import { adminOrganizationSummarySchema } from './api.js';
import { BILLING_INTERVALS, CONTRACT_STATUSES, billableActionSchema, billedProductKeySchema, billingCurrencySchema, billingDocumentSchema, billingPeriodQuerySchema, billingPeriodSchema, microsSchema, millitokensSchema, priceComponentSchema, ratedLineItemSchema, ratedPeriodSchema, selfServeTermsSchema, spendCapSchema, spendCapStatusSchema, trialTermsSchema, } from './billing.js';
import { brandingSchema } from './domains.js';
import { intelligenceLevelSchema } from './admin.js';
import { ORGANIZATION_ADMINISTRATION_PERMISSIONS, orgRolePermissions, orgRoleSchema, platformPermissionSchema, } from './roles.js';
/**
 * Milestone 2b: organization settings for customer administrators, under /v1/organizations/{orgId}/.
 *
 * Who: org_owner (every organization permission), org_admin (members, modules, domains, audit), billing_admin (the
 * overview now, billing from milestone 3), member (nothing). Trace staff never use these routes and customer
 * administrators never use /v1/admin. The routes take person tokens for the platform audience only (never staff or
 * service tokens), and platform-api refuses to register one without person authentication.
 *
 * Every request is decided by the server from the database at that moment: the organization in the path must be
 * one where the caller holds an active membership of an active user in an active organization (otherwise 404
 * not_found, exactly as for an organization that does not exist, so nothing tells a stranger which ids exist); an
 * organization that enforces its own SSO needs a session through its connection (403 sso_required with hints); the
 * membership's roles must hold an administrative permission (ORGANIZATION_ADMINISTRATION_PERMISSIONS) and the
 * permission of the operation (ORGANIZATION_ADMIN_OPERATIONS), otherwise 403 forbidden.
 *
 * The routes reuse the milestone 2 shapes (admin.ts) wherever they fit:
 *
 * | Route | Request | Answer |
 * |---|---|---|
 * | GET overview | | organizationOverviewSchema |
 * | GET members | memberListQuerySchema (keyset cursor, query, status, productKey, limit) | memberListSchema |
 * | GET, PATCH member | membershipChangeSchema | memberSchema |
 * | GET, POST invitations | invitationCreateSchema | invitationListSchema, invitationSummarySchema (201) |
 * | POST revokeInvitation | reasonBodySchema (optional) | invitationSummarySchema |
 * | GET modules | | organizationModulesSchema |
 * | GET, PATCH module | moduleConfigurationChangeSchema | effectiveModuleSchema, moduleConfigurationResultSchema |
 * | PATCH intelligenceLevel (0.19.0) | intelligenceLevelChangeSchema | organizationIntelligenceLevelSchema |
 * | GET, POST emergencyStops | emergencyStopRequestSchema | organizationEmergencyStopListSchema, organizationEmergencyStopSchema (201) |
 * | POST releaseEmergencyStop | reasonBodySchema (optional) | organizationEmergencyStopSchema |
 * | GET, POST domains | domainCreateSchema | adminDomainListSchema, domainCreatedSchema (201) |
 * | POST domainAction | domainActionSchema | adminDomainSchema |
 * | GET, POST emailDomains | emailDomainCreateSchema | emailDomainListSchema, emailDomainSchema (201) |
 * | POST emailDomainAction | emailDomainActionSchema | emailDomainSchema |
 * | GET audit | organizationAuditQuerySchema | auditPageSchema |
 * | GET billing (0.10.0) | | organizationBillingSchema |
 * | GET billingUsage | organizationBillingUsageQuerySchema | organizationPeriodUsageSchema |
 * | GET billingLineItems | organizationBillingUsageQuerySchema | organizationRatedPeriodSchema |
 * | GET billingDocuments | | organizationBillingDocumentListSchema |
 * | PATCH billingSpendCap | spendCapChangeSchema | organizationContractSchema |
 * | PATCH billingCarrierConnections (unreleased) | carrierConnectionsChangeSchema | organizationContractSchema |
 * | POST billingPaymentSession (unreleased) | | paymentSessionSchema |
 * | GET billingPaymentMethod (unreleased) | billingPaymentMethodQuerySchema | billingPaymentMethodSchema |
 *
 * Module views list only the modules released to the organization in this environment (a module that is not is
 * answered like an unknown one), and name Trace staff as "Trace", never by their names. Billing views (milestone 3b,
 * 0.10.0) show the organization its own contracts, usage, line items and invoices with nothing of the billing
 * provider and no name of a Trace staff member; the section at the end of this file says what they carry.
 */
/** Every organization administration route starts with this prefix. */
export const ORGANIZATION_ADMIN_API_PATH_PREFIX = '/v1/organizations/';
/**
 * True for an organization administration path or route pattern: requests to it carry a person token for the
 * platform audience, and platform-api refuses to register a route here without person authentication.
 */
export function isOrganizationAdminApiPath(path) {
    return (path === ORGANIZATION_ADMIN_API_PATH_PREFIX.slice(0, -1) ||
        path.startsWith(ORGANIZATION_ADMIN_API_PATH_PREFIX));
}
const segment = encodeURIComponent;
const organization = (orgId) => `/v1/organizations/${segment(orgId)}`;
/** The route table of the organization administration API. The server registers exactly these paths. */
export const PLATFORM_ORGANIZATION_ADMIN_API = {
    overview: (orgId) => organization(orgId),
    members: (orgId) => `${organization(orgId)}/members`,
    member: (orgId, membershipId) => `${organization(orgId)}/members/${segment(membershipId)}`,
    invitations: (orgId) => `${organization(orgId)}/invitations`,
    revokeInvitation: (orgId, invitationId) => `${organization(orgId)}/invitations/${segment(invitationId)}/revoke`,
    modules: (orgId) => `${organization(orgId)}/modules`,
    module: (orgId, moduleKey) => `${organization(orgId)}/modules/${segment(moduleKey)}`,
    // 0.19.0 (self-serve sign-up piece 2): the organization's intelligence level, one per product.
    intelligenceLevel: (orgId) => `${organization(orgId)}/intelligence-level`,
    emergencyStops: (orgId) => `${organization(orgId)}/emergency-stops`,
    releaseEmergencyStop: (orgId, stopId) => `${organization(orgId)}/emergency-stops/${segment(stopId)}/release`,
    domains: (orgId) => `${organization(orgId)}/domains`,
    domainAction: (orgId, domainId) => `${organization(orgId)}/domains/${segment(domainId)}/actions`,
    emailDomains: (orgId) => `${organization(orgId)}/email-domains`,
    emailDomainAction: (orgId, emailDomainId) => `${organization(orgId)}/email-domains/${segment(emailDomainId)}/actions`,
    audit: (orgId) => `${organization(orgId)}/audit`,
    // 0.10.0 (milestone 3b): billing for the organization's own administrators.
    billing: (orgId) => `${organization(orgId)}/billing`,
    billingUsage: (orgId) => `${organization(orgId)}/billing/usage`,
    billingLineItems: (orgId) => `${organization(orgId)}/billing/line-items`,
    billingDocuments: (orgId) => `${organization(orgId)}/billing/documents`,
    billingSpendCap: (orgId, contractId) => `${organization(orgId)}/billing/contracts/${segment(contractId)}/spend-cap`,
    // Unreleased: the organization's own carrier connections and its payment management.
    billingCarrierConnections: (orgId, contractId) => `${organization(orgId)}/billing/contracts/${segment(contractId)}/carrier-connections`,
    billingPaymentSession: (orgId) => `${organization(orgId)}/billing/payment-session`,
    billingPaymentMethod: (orgId) => `${organization(orgId)}/billing/payment-method`,
};
/**
 * The permission each operation needs, on top of being an administrator of the organization. platform-api decides
 * with the same permissions (a test keeps its operation map equal to this one). Module reads need
 * modules.configure, so billing administrators and members see no module administration. Billing views (0.10.0) need
 * billing.read (org_owner, org_admin, billing_admin) and the spend cap billing.manage (org_owner, billing_admin), as
 * do (unreleased) the carrier connections and the payment management session.
 */
export const ORGANIZATION_ADMIN_OPERATIONS = {
    readOverview: { method: 'GET', route: 'overview', permission: 'org.read' },
    listMembers: { method: 'GET', route: 'members', permission: 'members.read' },
    readMember: { method: 'GET', route: 'member', permission: 'members.read' },
    changeMember: { method: 'PATCH', route: 'member', permission: 'members.manage' },
    listInvitations: { method: 'GET', route: 'invitations', permission: 'members.read' },
    createInvitation: { method: 'POST', route: 'invitations', permission: 'members.manage' },
    revokeInvitation: { method: 'POST', route: 'revokeInvitation', permission: 'members.manage' },
    listModules: { method: 'GET', route: 'modules', permission: 'modules.configure' },
    readModule: { method: 'GET', route: 'module', permission: 'modules.configure' },
    configureModule: { method: 'PATCH', route: 'module', permission: 'modules.configure' },
    setIntelligenceLevel: { method: 'PATCH', route: 'intelligenceLevel', permission: 'modules.configure' },
    listEmergencyStops: { method: 'GET', route: 'emergencyStops', permission: 'modules.configure' },
    createEmergencyStop: { method: 'POST', route: 'emergencyStops', permission: 'modules.configure' },
    releaseEmergencyStop: { method: 'POST', route: 'releaseEmergencyStop', permission: 'modules.configure' },
    listDomains: { method: 'GET', route: 'domains', permission: 'domains.read' },
    createDomain: { method: 'POST', route: 'domains', permission: 'domains.manage' },
    actOnDomain: { method: 'POST', route: 'domainAction', permission: 'domains.manage' },
    listEmailDomains: { method: 'GET', route: 'emailDomains', permission: 'domains.read' },
    addEmailDomain: { method: 'POST', route: 'emailDomains', permission: 'domains.manage' },
    actOnEmailDomain: { method: 'POST', route: 'emailDomainAction', permission: 'domains.manage' },
    readAudit: { method: 'GET', route: 'audit', permission: 'audit.read' },
    readBilling: { method: 'GET', route: 'billing', permission: 'billing.read' },
    readBillingUsage: { method: 'GET', route: 'billingUsage', permission: 'billing.read' },
    readBillingLineItems: { method: 'GET', route: 'billingLineItems', permission: 'billing.read' },
    readBillingDocuments: { method: 'GET', route: 'billingDocuments', permission: 'billing.read' },
    readPaymentMethod: { method: 'GET', route: 'billingPaymentMethod', permission: 'billing.read' },
    changeSpendCap: { method: 'PATCH', route: 'billingSpendCap', permission: 'billing.manage' },
    changeCarrierConnections: {
        method: 'PATCH',
        route: 'billingCarrierConnections',
        permission: 'billing.manage',
    },
    createPaymentSession: { method: 'POST', route: 'billingPaymentSession', permission: 'billing.manage' },
};
/**
 * The operations a set of organization permissions allows (me/access gives them per organization): none without
 * an administrative permission, otherwise those whose permission it holds. For showing and hiding controls only.
 */
export function organizationAdminOperationsWith(permissions) {
    if (!ORGANIZATION_ADMINISTRATION_PERMISSIONS.some((permission) => permissions.includes(permission)))
        return [];
    return Object.keys(ORGANIZATION_ADMIN_OPERATIONS).filter((name) => permissions.includes(ORGANIZATION_ADMIN_OPERATIONS[name].permission));
}
/** The operations a role set allows (organizationAdminOperationsWith of its permissions). */
export function organizationAdminOperationsFor(roles) {
    return organizationAdminOperationsWith(orgRolePermissions(roles));
}
/* ------------------------------------------------------------------------------------------------ */
/* Overview                                                                                          */
/* ------------------------------------------------------------------------------------------------ */
/**
 * GET overview: the organization (as Trace Admin summarizes it), its branding and sign-in policy, and the viewer:
 * the caller's membership, roles and permissions as the database holds them at this moment. memberCount counts the
 * memberships that are not removed; products are the organization's subscriptions.
 */
export const organizationOverviewSchema = adminOrganizationSummarySchema.extend({
    branding: brandingSchema,
    ssoPolicy: z.object({ enforced: z.boolean(), provider: z.string(), connection: z.string() }).nullable(),
    viewer: z.object({
        membershipId: z.uuid(),
        roles: z.array(orgRoleSchema),
        permissions: z.array(platformPermissionSchema),
    }),
    /** The version of the role map the permissions come from (ROLE_MAP_VERSION). */
    roleMapVersion: z.number().int().positive(),
});
/* ------------------------------------------------------------------------------------------------ */
/* Emergency stops                                                                                   */
/* ------------------------------------------------------------------------------------------------ */
/**
 * Who placed an emergency stop: the organization's own administrators, or Trace (staff, or a stop with no record of
 * an organization administrator placing it). An organization stops its modules or itself and releases its own stops,
 * never one Trace placed (403 stopped_by_trace).
 */
export const STOP_PLACERS = ['organization', 'trace'];
/** One emergency stop as an organization sees it: Trace staff are named "Trace", never by their names. */
export const organizationEmergencyStopSchema = emergencyStopSchema.extend({ placedBy: z.enum(STOP_PLACERS) });
/** GET emergency stops of the organization, active and released, newest first. */
export const organizationEmergencyStopListSchema = z.object({
    items: z.array(organizationEmergencyStopSchema),
});
/* ------------------------------------------------------------------------------------------------ */
/* Intelligence level (0.19.0)                                                                       */
/* ------------------------------------------------------------------------------------------------ */
/**
 * PATCH intelligence level (0.19.0, spec 2026-10-18 piece 2): the organization's level for one product, with a reason.
 * platform-api writes it to the configuration of every module of the product that carries a level and is released to
 * the organization (a version, an audit row and an event each), and turns no module on or off. 400 for a product
 * without such modules, 409 when none of them is in the organization's plan.
 */
export const intelligenceLevelChangeSchema = z.strictObject({
    productKey: billedProductKeySchema,
    intelligenceLevel: intelligenceLevelSchema,
    reason: z.string().min(3).max(500),
});
/** The answer: the level as written, and the modules it was written to, in catalog order. */
export const organizationIntelligenceLevelSchema = z.object({
    organizationId: z.uuid(),
    productKey: billedProductKeySchema,
    intelligenceLevel: intelligenceLevelSchema,
    modules: z.array(z.string()),
});
/* ------------------------------------------------------------------------------------------------ */
/* Email domains                                                                                     */
/* ------------------------------------------------------------------------------------------------ */
export const EMAIL_DOMAIN_VERIFICATION_STATES = ['pending', 'verified', 'failed'];
/** The DNS record that proves an organization controls an email domain. */
export const emailDomainVerificationRecordSchema = z.object({
    recordType: z.literal('TXT'),
    recordName: z.string(),
    recordValue: z.string(),
});
/**
 * One email domain of the organization: a verified one routes sign-in discovery to the organization's SSO (it never
 * grants membership). verification is the TXT record to publish while the domain is not verified (it belongs to this
 * claim only, so it can be shown again), and null once it is.
 */
export const emailDomainSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    domain: z.string(),
    verificationStatus: z.enum(EMAIL_DOMAIN_VERIFICATION_STATES),
    verifiedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    verification: emailDomainVerificationRecordSchema.nullable(),
});
export const emailDomainListSchema = z.object({ items: z.array(emailDomainSchema) });
/** POST email domains: a domain the organization claims, pending until its TXT record is verified. */
export const emailDomainCreateSchema = z.object({ domain: z.string().min(3).max(253) });
/**
 * verify reads the TXT record (409 precondition_failed while it is not published, 503 when the lookup fails; nothing
 * changes then); remove deletes the claim (a verified domain stops routing sign-in discovery at once) and answers it
 * as it was.
 */
export const EMAIL_DOMAIN_ACTIONS = ['verify', 'remove'];
export const emailDomainActionSchema = z.object({
    action: z.enum(EMAIL_DOMAIN_ACTIONS),
    reason: z.string().min(3).max(500),
});
/* ------------------------------------------------------------------------------------------------ */
/* Audit                                                                                             */
/* ------------------------------------------------------------------------------------------------ */
/**
 * GET audit: the organization's audit log (the organization comes from the path), newest first, filtered by action,
 * actor and time (from inclusive, to exclusive), keyset pages of at most 100. The answer is auditPageSchema; Trace
 * staff and services appear as "Trace", never by their names or ids.
 */
export const organizationAuditQuerySchema = auditQuerySchema.omit({ organizationId: true });
/* ------------------------------------------------------------------------------------------------ */
/* Billing (0.10.0, milestone 3b)                                                                    */
/* ------------------------------------------------------------------------------------------------ */
/*
 * What an organization's administrators see of their billing, and the one thing they change (the spend cap). The
 * staff shapes of billing.ts carry the provider's references, the staff who approved and the plan version's
 * provenance; these shapes carry none of that: no provider name or reference, no name of a Trace staff member, no
 * internal id beyond the contract, the period and the document the screens need. The prices come from the plan
 * version of each of the organization's own contracts, never from the catalog at large.
 */
/**
 * The tokens one action uses as the organization runs it: at the level its module is configured at (the module's
 * intelligenceLevel setting, Seasoned when unset), or null for an action without levels.
 */
export const organizationTokenEstimateSchema = z.object({
    action: billableActionSchema,
    moduleKey: z.string(),
    level: intelligenceLevelSchema.nullable(),
    millitokens: millitokensSchema,
    /** Unreleased: units included in every period at no tokens (300 assistant replies); null when none. */
    includedPerPeriod: z.number().int().positive().nullable().default(null),
});
/**
 * The price summary of a plan version: the components as the version prices them (labels and prices, the token
 * price's parameters), the token estimate of every action at the organization's configured level, the trial and the
 * self-serve terms. Not the version's source label, its billable basis or the modules it allows, and nothing of a
 * module not released to the organization (no estimate of its actions, no discount conditioned on it).
 */
export const planPriceSummarySchema = z.object({
    currency: billingCurrencySchema,
    billingInterval: z.enum(BILLING_INTERVALS),
    components: z.array(priceComponentSchema),
    tokenEstimates: z.array(organizationTokenEstimateSchema),
    trial: trialTermsSchema.nullable(),
    selfServe: selfServeTermsSchema.nullable(),
});
/**
 * One contract as the organization sees it. billingStopped is true while the billing provider no longer collects
 * for the contract (its subscription canceled, unpaid or expired): no new work is admitted until billing is restored.
 * version is what a spend cap change names as expectedVersion.
 */
export const organizationContractSchema = z.object({
    id: z.uuid(),
    productKey: billedProductKeySchema,
    plan: z.object({
        name: z.string(),
        /** The plan version, as a label (v2). */
        versionLabel: z.string(),
        prices: planPriceSummarySchema,
    }),
    status: z.enum(CONTRACT_STATUSES),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    trial: z
        .object({
        endsAt: z.iso.datetime(),
        cardRequired: z.boolean(),
        /** Unreleased (additive): Trace ended the trial early, so billing started at endsAt (default false). */
        endedEarly: z.boolean().default(false),
    })
        .nullable(),
    currentPeriod: billingPeriodSchema.nullable(),
    carrierConnections: z.number().int().nonnegative(),
    spendCap: spendCapSchema.nullable(),
    billingStopped: z.boolean(),
    version: z.number().int().positive(),
});
/**
 * GET billing: the organization's contracts in force, scheduled, and ended within the last year, newest first.
 * paymentManagement (unreleased): whether its administrators can manage payment (cards, invoices) on the billing
 * provider's own page through POST billingPaymentSession; false while the environment has no billing provider that
 * offers it or the organization has no billing account there yet (an older server answers without it: false).
 */
export const organizationBillingSchema = z.object({
    contracts: z.array(organizationContractSchema),
    paymentManagement: z.boolean().default(false),
});
/**
 * GET billing usage and line items: the contract (the one in force when absent, else the latest) and the start of
 * the period (the current period when absent), as the staff views take them; no event pages here.
 */
export const organizationBillingUsageQuerySchema = billingPeriodQuerySchema.pick({
    contractId: true,
    periodStart: true,
});
/**
 * GET billing usage: the period's tokens billed and still held, the cost so far (the token price of the billed
 * tokens, and of billed plus held), the cap with what remains, and the usage by action. No usage events: those
 * carry the producing service and its reservations, which are the platform's, not the customer's.
 */
export const organizationPeriodUsageSchema = z.object({
    contractId: z.uuid(),
    period: billingPeriodSchema,
    status: z.enum(['open', 'closed']),
    currency: billingCurrencySchema,
    billableMillitokens: millitokensSchema,
    heldMillitokens: millitokensSchema,
    committedMillitokens: millitokensSchema,
    /** The token price of the billed tokens, cost(T). */
    billedCostMicros: microsSchema.nonnegative(),
    /** The token price of billed plus held tokens, cost(C): what the cap is compared with. */
    committedCostMicros: microsSchema.nonnegative(),
    spendCap: spendCapStatusSchema.nullable(),
    byAction: z.array(z.object({
        action: billableActionSchema,
        moduleKey: z.string(),
        events: z.number().int().nonnegative(),
        billableMillitokens: millitokensSchema,
        /** Unreleased: units the period's allowance covered, and the allowance (null when the action has none). */
        includedQuantity: z.number().int().nonnegative().default(0),
        includedPerPeriod: z.number().int().positive().nullable().default(null),
    })),
    shipments: z.number().int().nonnegative(),
    beyondSelfServe: z.boolean(),
});
/**
 * A line item without its detail (price function versions, adjustment ids: the platform's working). An adjustment's
 * description is "Credit" or "Debit" only: its reason is staff free text and never reaches the organization.
 */
export const organizationLineItemSchema = ratedLineItemSchema.omit({ detail: true });
/** GET billing line items: a closed period's lines as rated, or the open period's preview (id and ratedAt null). */
export const organizationRatedPeriodSchema = ratedPeriodSchema
    .omit({ planVersionId: true, lines: true })
    .extend({ lines: z.array(organizationLineItemSchema) });
/**
 * An invoice as the organization sees it: status, totals, period, and the provider's hosted page over https only
 * (platform-api drops any other link). Nothing names the provider or its own invoice id.
 */
export const organizationBillingDocumentSchema = billingDocumentSchema
    .omit({ organizationId: true, provider: true, providerDocumentId: true, hostedUrl: true })
    .extend({ hostedUrl: z.url({ protocol: /^https$/ }).nullable() });
export const organizationBillingDocumentListSchema = z.object({
    items: z.array(organizationBillingDocumentSchema),
});
/**
 * PATCH the spend cap of a contract: the version read (409 when it changed), the monthly cap in micros of the
 * contract's currency or null for no cap, and a reason. The cap must leave room for the token price's minimum charge
 * (400 otherwise); a lowered cap blocks new admissions only, work already admitted finishes.
 */
export const spendCapChangeSchema = z.strictObject({
    expectedVersion: z.number().int().positive(),
    spendCapMicros: microsSchema.positive().nullable(),
    reason: z.string().min(3).max(500),
});
/**
 * PATCH the carrier connections of a contract (unreleased): the version read (409 when it changed), the number of carrier
 * connections the organization buys (at most the plan's maximum, 400 otherwise; the contract must not have ended) and
 * a reason. The period bills the most connections purchased in it; a lowered number bills from the next period.
 */
export const carrierConnectionsChangeSchema = z.strictObject({
    expectedVersion: z.number().int().positive(),
    carrierConnections: z.number().int().nonnegative().max(100_000),
    reason: z.string().min(3).max(500),
});
/**
 * POST billing payment session (unreleased): a short-lived page of the billing provider where the organization's
 * administrators add or change the card it is charged with and see its invoices (no plan or contract change: those
 * are Trace's). The browser opens url; the page returns to the account hub's billing page.
 */
export const paymentSessionSchema = z.object({ url: z.url({ protocol: /^https$/ }) });
/**
 * GET billingPaymentMethod (unreleased): the card the organization is charged with at the billing provider, the one
 * its administrators manage through the payment page (POST billingPaymentSession): its brand, last four digits and
 * expiry, or none. Never the full number, never more than the provider shows its own customer. billing.read; 409
 * conflict without a billing account at the provider yet, 503 dependency_unavailable where the environment has no
 * payment page or the provider does not answer. Kept for about a minute; fresh=true reads it again (the hub asks
 * so when the person comes back from the payment page).
 */
export const billingPaymentMethodQuerySchema = z.object({
    fresh: z.enum(['true', 'false']).optional(),
});
export const billingPaymentMethodSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('card'),
        brand: z.string().min(1).max(40),
        last4: z.string().regex(/^[0-9]{4}$/),
        expMonth: z.number().int().min(1).max(12),
        expYear: z.number().int().min(2000).max(2200),
    }),
    z.object({ status: z.literal('none') }),
]);
//# sourceMappingURL=organization-admin.js.map